import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { safeFetch } from "@/api/safeFetch";
import {
  ButtonPopup,
  ButtonSiguiente,
  Input,
  TextareaComun,
} from "@/components/ui";
import { useProfesional } from "../context/ProfesionalContext";
import Alerta from "@/components/alert/Alerta";

const EditarServiciosLanding: FC = () => {
  const navigate = useNavigate();
  const { perfilId } = useProfesional();
  const { profesionalId, profesionId } = useParams();
  const [section, setSection] = useState<any>(null);
  const MAX_SERVICIOS = 6;

  const [alert, setAlert] = useState<{
    mensaje: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);

  useEffect(() => {
    if (!perfilId) return;

    const load = async () => {
      try {
        const res = await safeFetch(`/api/landing/${perfilId}/sections`);

        const data = await res.json();

        let services = data.find((s: any) => s.type === "services");

        let created = false;

        // 🔥 CREATE IF NOT EXISTS
        if (!services) {
          const createRes = await safeFetch(
            `/api/landing/${perfilId}/section`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                type: "services",
                props: {
                  title: "",
                  subtitle: "",
                  items: [],
                },
              }),
            },
          );

          if (!createRes.ok) {
            throw new Error("Error creando services");
          }

          services = await createRes.json();
          created = true;
        }

        // 🔥 NORMALIZE SAFE
        const normalizedProps = {
          title: services?.props?.title ?? "",
          subtitle: services?.props?.subtitle ?? "",
          items: services?.props?.items ?? [],
        };

        setSection({
          ...services,
          props: normalizedProps,
        });

        if (created) {
          setAlert({
            mensaje: "✅ Sección de servicios creada automáticamente",
            type: "success",
          });
        }
      } catch (err) {
        console.error(err);

        setAlert({
          mensaje: "❌ Error cargando servicios",
          type: "error",
        });
      }
    };

    load();
  }, [perfilId]);

  if (!section) {
    return <div className="text-zinc-800">Cargando sección...</div>;
  }

  // 🔧 UPDATE SECTION FIELDS
  const updateField = (field: "title" | "subtitle", value: string) => {
    setSection((prev: any) => ({
      ...prev,
      props: {
        ...prev.props,
        [field]: value,
      },
    }));
  };

  // 🔧 UPDATE ITEM
  const updateService = (
    index: number,
    field: "titulo" | "descripcion",
    value: string,
  ) => {
    setSection((prev: any) => {
      const items = [...(prev.props.items || [])];

      items[index] = {
        ...items[index],
        [field]: value,
      };

      return {
        ...prev,
        props: {
          ...prev.props,
          items,
        },
      };
    });
  };

  // 🔧 ADD ITEM
  const addService = () => {
    if ((section.props.items?.length || 0) >= MAX_SERVICIOS) {
      return;
    }
    setSection((prev: any) => ({
      ...prev,
      props: {
        ...prev.props,
        items: [
          ...(prev.props.items || []),
          {
            titulo: "",
            descripcion: "",
          },
        ],
      },
    }));
  };

  // 🔧 REMOVE ITEM
  const removeService = (index: number) => {
    setSection((prev: any) => {
      const items = [...(prev.props.items || [])];
      items.splice(index, 1);

      return {
        ...prev,
        props: {
          ...prev.props,
          items,
        },
      };
    });
  };

  // 🔥 SAVE
  const save = async () => {
    if (!section?.id) {
      setAlert({
        mensaje: "❌ No existe section.id",
        type: "error",
      });
      return;
    }

    try {
      const res = await safeFetch(`/api/landing/section/${section.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          props: section.props,
        }),
      });

      if (!res.ok) throw new Error();

      await res.json();

      window.dispatchEvent(new Event("landingUpdated"));

      setAlert({
        mensaje: "✅ Cambios guardados correctamente",
        type: "success",
      });
    } catch (err) {
      console.error(err);

      setAlert({
        mensaje: "❌ Error al guardar cambios",
        type: "error",
      });
    }
  };

  const items = section?.props?.items || [];

  const card = `relative overflow-hidden rounded-lg border-r-4 border-t-2 border-cyan-300/50 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-3xl shadow-md shadow-zinc-800 p-8`;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 text-white">
      {/* VOLVER */}
      <div className="absolute top-4 left-4 z-50 pointer-events-auto">
        <ButtonSiguiente
          onClick={() =>
            navigate(
              `/editar-landing/${profesionalId}/${profesionId}/${perfilId}`,
              {
                state: {
                  showLandingPrincipal: true,
                },
              },
            )
          }
          className="
      px-4 py-2
      rounded-lg
      bg-zinc-900
      border border-white/10
      hover:border-cyan-500
      transition
    "
        >
          ← Volver
        </ButtonSiguiente>
      </div>

      {/* HEADER */}
      <div
        className="
         w-full
         rounded-lg
         border-t-2
         border-b-2
         border-cyan-300
         bg-zinc-950
         py-2
         px-8
         shadow-md
         shadow-zinc-800
         backdrop-blur-xl
         mb-10
         mt-5
       "
      >
        <div
          className="
           flex flex-col
           gap-8
           md:flex-row
           md:items-center
           md:justify-between
         "
        >
          <div className="space-y-3 flex-1">
            <h1 className="text-3xl font-bold tracking-tight">
              Editor de Servicios
            </h1>

            <p className="text-zinc-400 text-sm max-w-md">
              Gestiona los servicios visibles de tu landing.
            </p>
          </div>

          <div
            className="
        flex
        flex-col
        sm:flex-row
        gap-3
        w-full
        md:w-auto
      "
          >
            <ButtonPopup
              onClick={addService}
              disabled={(section.props.items?.length || 0) >= MAX_SERVICIOS}
              className="
               px-4 py-2
               rounded-lg
               bg-zinc-900
               border border-white/10
               hover:border-green-500
               transition
               flex items-center justify-center
               whitespace-nowrap
               overflow-hidden
               text-ellipsis
               max-w-full"
            >
               {(section.props.items?.length || 0) >= MAX_SERVICIOS
                ? "Máximo alcanzado"
                : "Añadir Servicio"}
            </ButtonPopup>
            
            <ButtonPopup
              onClick={save}
              className=" 
              px-4 py-2
              rounded-lg
              bg-zinc-900
              border border-white/10
              hover:border-cyan-500
              transition
              flex items-center justify-center
              whitespace-nowrap
              overflow-hidden
              text-ellipsis
              max-w-full"
            >
              Guardar cambios
            </ButtonPopup>
          </div>
        </div>
      </div>

      {/* CONFIGURACIÓN */}
      <section className={`${card} text-white bg-zinc-900`}>
        <h2 className="text-xl font-semibold mb-2">
          Información de la sección
        </h2>

        <p className="text-zinc-400 text-sm mb-8">
          Configura el título y el subtítulo de la sección de servicios.
        </p>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-300">Título</label>

            <Input
              value={section?.props?.title || ""}
              onChange={(e) => updateField("title", e.target.value)}
              className="text-zinc-800"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-300">
              Subtítulo
            </label>

            <TextareaComun
              value={section?.props?.subtitle || ""}
              onChange={(e) => updateField("subtitle", e.target.value)}
              className="text-zinc-800"
            />
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-8 mt-10">
        {items.length === 0 && (
          <section className={card}>
            <p className="text-zinc-500">No hay servicios creados todavía.</p>
          </section>
        )}

        {items.map((item: any, index: number) => (
          <section
            key={index}
            className={`${card} space-y-5 text-white bg-zinc-900`}
          >
            <h3 className="text-lg font-semibold">Servicio {index + 1}</h3>

            <Input
              value={item.titulo || ""}
              placeholder="Título"
              onChange={(e) => updateService(index, "titulo", e.target.value)}
              className="text-zinc-800"
            />

            <TextareaComun
              value={item.descripcion || ""}
              placeholder="Descripción"
              onChange={(e) =>
                updateService(index, "descripcion", e.target.value)
              }
              className="text-zinc-800"
            />

            <div className="flex justify-end">
              <ButtonPopup
                onClick={() => removeService(index)}
                className="hover:border-red-500"
              >
                Eliminar
              </ButtonPopup>
            </div>
          </section>
        ))}
      </div>

      {/* ALERTA */}
      {alert && (
        <Alerta
          mensaje={alert.mensaje}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default EditarServiciosLanding;
